import type { LucideIcon } from 'lucide-react';
import { ReactNode } from 'react';
import z from 'zod';

import type { QueryRequestOptions } from '@/gateways/BaseGateway';
import type { BaseParams } from '@/shared/hooks/queries/useAutoCompleteQuery';

export type FilterVariant = 'select' | 'multiselect' | 'number' | 'boolean' | 'text';

export type FilterOperator =
  | 'eq'
  | 'ne'
  | 'in'
  | 'notIn'
  | 'iLike'
  | 'notILike'
  | 'lt'
  | 'lte'
  | 'gt'
  | 'gte';

export const SUPPORTED_OPERATORS = {
  select: ['eq', 'ne'] as const,
  multiselect: ['in', 'notIn'] as const,
  text: ['iLike', 'notILike', 'eq', 'ne'] as const,
  number: ['eq', 'ne', 'lt', 'lte', 'gt', 'gte'] as const,
  boolean: ['eq'] as const,
} as const satisfies Record<FilterVariant, FilterOperator[]>;

type SupportedOperator<V extends FilterVariant> = (typeof SUPPORTED_OPERATORS)[V][number];

type FilterValue<V extends FilterVariant> =
  | {
      select: string[];
      multiselect: string[];
      text: string;
      number: number;
      boolean: boolean;
    }[V]
  | undefined;

export type SelectOption = string;

type SelectOptionFn = (
  input: BaseParams,
  options?: QueryRequestOptions,
) => Promise<{
  items: SelectOption[];
}>;

type BooleanFilterLabels = {
  truthy: string;
  falsey: string;
};

export type FilterDef<V extends FilterVariant> = {
  variant: V;
  label: string;
  icon: LucideIcon;
} & (V extends 'select' | 'multiselect'
  ? {
      options: SelectOption[] | SelectOptionFn;
      optionLabel?: (option: SelectOption) => ReactNode;
    }
  : { options?: never; optionLabel?: never }) &
  (V extends 'boolean' ? { labels: BooleanFilterLabels } : { labels?: never });

export type FilterDefs = Record<string, FilterDef<FilterVariant>>;

export type FilterId<T extends FilterDefs> = Extract<keyof T, string>;

export type Filter<TFilterDefs extends FilterDefs, TId extends FilterId<TFilterDefs>> = {
  id: TId;
  operator: SupportedOperator<TFilterDefs[TId]['variant']>;
  value: FilterValue<TFilterDefs[TId]['variant']>;
};

/**
 * FilterState is an Array of valid filters, according to the provided FilterDefs.
 *
 * The type explained:
 * ```ts
 * // Given these filterDefs
 * {
 *   name: { id: 'name', variant: 'text' //... },
 *   age: { id: 'age', variant: 'number' //... }
 * }
 *
 * // They become a mapped type
 * {
 *   [FilterDefId in keyof TFilterDefs]: Filter<TFilterDefs, FilterDefId>;
 * }
 *
 * // results in the type
 * {
 *   name: Filter<TFilterDefs, 'name'>;
 *   age: Filter<TFilterDefs, 'age'>;
 * }
 *
 * // Then the above type is extracted as a union
 * {
 *   name: Filter<TFilterDefs, 'name'>;
 *   age: Filter<TFilterDefs, 'age'>;
 * }[keyof TFilterDefs]
 *
 * // becomes the type:
 * Filter<TFilterDefs, 'name'> | Filter<TFilterDefs, 'age'>;
 *
 * // So the final type is
 *  Array<
 *   | Filter<TFilterDefs, 'name'>
 *   | Filter<TFilterDefs, 'age'>
 *  >
 * ```
 *
 *
 * Note - FilterState supports multiple Filter items with the same ID but we do not yet
 * utilise this feature in the app. For example:
 * ```ts
 * [
 *   { id: 'qty' operator: 'gt' value: 10 },
 *   { id: 'qty' operator: 'lt' value: 20 }
 * ]
 * ```
 */
export type FilterState<TFilterDefs extends FilterDefs> = Array<
  {
    [FilterDefId in FilterId<TFilterDefs>]: Filter<TFilterDefs, FilterDefId>;
  }[FilterId<TFilterDefs>]
>;

/**
 * A filter that has a value
 */
export type ActiveFilter<TFilterDefs extends FilterDefs, TId extends FilterId<TFilterDefs>> = Omit<
  Filter<TFilterDefs, TId>,
  'value'
> & {
  value: NonNullable<Filter<TFilterDefs, TId>['value']>;
};

export const filterValueSchemas = {
  select: z.array(z.string().trim().min(1)).optional(),
  multiselect: z.array(z.string().trim().min(1)).optional(),
  text: z.string().trim().min(1).optional(),
  number: z.number().int().optional(),
  boolean: z.boolean().optional(),
} as const;

export const filterOperatorSchemas = {
  select: z.enum(SUPPORTED_OPERATORS.select),
  multiselect: z.enum(SUPPORTED_OPERATORS.multiselect),
  text: z.enum(SUPPORTED_OPERATORS.text),
  number: z.enum(SUPPORTED_OPERATORS.number),
  boolean: z.enum(SUPPORTED_OPERATORS.boolean),
} as const;

export const unsafeFilterSchema = z.object({
  id: z.string(),
  operator: z.string(),
  value: z.unknown().optional(),
});

/**
 * filter input with the correct shape, but potentially has an
 * invalid operator or value that may result in errors when
 * processed by consumers (such as the backend). use-filters.ts
 * validates unsafe filters before storing them in the filter state.
 */
export type UnsafeFilter = z.infer<typeof unsafeFilterSchema>;
